// Set-equipment catalogue. ADR 0018 §1 / docs/04 §4.4.
//
// 22 `SetDefinition` templates across 5 categories (A ジョブ体現 9 / B 軸 4 /
// C 属性 3 / D 戦術 3 / E ギミック 3). Each set's `bonuses` is a strengthening
// ladder in ascending `piecesRequired`: the first tier grants the concept
// skill (set-skills.ts), upper tiers reinforce it plus stat %.
//
// ID schema (ADR 0018 §3 / §J):
//   set.<keyword>   for the set; the concept skill is
//   set-skill.<setId>.<keyword>   (prefix `set-skill.<id>` must match).
//
// Bonus descriptions and stage cut-offs are provisional — numeric calibration
// is Phase 4 scope (ADR 0018 §未確定事項). Drop / roll logic and set-only base
// items are likewise out of scope.
// =============================================================================
// カテゴリ A: T3/T4 ジョブ体現セット (9) — 大型・3-4 段階
// =============================================================================
const CATEGORY_A = [
    {
        id: 'set.aegis-vow',
        name: '聖盾の誓約',
        slots: ['head', 'chest', 'hands', 'boots', 'belt', 'offHand'],
        conceptSkillId: 'set-skill.set.aegis-vow.covenant-fill',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「誓約充填」獲得。被弾累積が閾値に達すると最も HP の低い味方に小シールドを付与。',
            },
            { piecesRequired: 4, description: '誓約充填のシールド量増加 + 防御系ステータス %。' },
            { piecesRequired: 6, description: '誓約充填の発動閾値低下 + 全味方への波及。' },
        ],
        typeAffinity: ['Tank'],
    },
    {
        id: 'set.seven-phase-crown',
        name: '七相の王冠',
        slots: ['weaponMain', 'head', 'chest', 'amulet', 'ring1', 'cloak'],
        conceptSkillId: 'set-skill.set.seven-phase-crown.phase-chant',
        conceptSkillKind: 'active',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「七相輪唱」獲得。発動ごとに強化属性が 7 属性を巡回。',
            },
            { piecesRequired: 4, description: '七相輪唱の威力増 + 属性ダメージ %。' },
            { piecesRequired: 6, description: '属性の巡回速度上昇、全属性を同時強化。' },
        ],
        typeAffinity: ['MagicDPS'],
    },
    {
        id: 'set.overflowing-grace',
        name: '余剰恩寵',
        slots: ['head', 'chest', 'hands', 'boots', 'amulet'],
        conceptSkillId: 'set-skill.set.overflowing-grace.surplus-grace',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「余剰恩寵」獲得。overheal 分をシールド化。',
            },
            { piecesRequired: 3, description: 'シールド変換率増 + 回復力 %。' },
            { piecesRequired: 5, description: '戦闘後半は overheal 分を攻撃力に変換。' },
        ],
        typeAffinity: ['Healer'],
    },
    {
        id: 'set.afterimage',
        name: '残影',
        slots: ['weaponMain', 'head', 'chest', 'hands', 'boots', 'cloak'],
        conceptSkillId: 'set-skill.set.afterimage.afterimage-mark',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「残影刻」獲得。回避成功時に残像が 1 回追撃。',
            },
            { piecesRequired: 4, description: '残像追撃の威力増 + 回避率 %。' },
            { piecesRequired: 6, description: '残像の追撃回数 +1。' },
        ],
        typeAffinity: ['MeleeDPS'],
    },
    {
        id: 'set.feast-of-the-dead',
        name: '死霊の宴',
        slots: ['weaponMain', 'head', 'chest', 'hands', 'cloak', 'ring1'],
        conceptSkillId: 'set-skill.set.feast-of-the-dead.bone-legion',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「骨軍律」獲得。敵死亡時に一定確率で骸骨を自動召喚 (owner 別召喚上限内、陣営召喚上限 20 を超えない)。',
            },
            { piecesRequired: 4, description: '召喚確率増 + 召喚体ステータス %。' },
            { piecesRequired: 6, description: '骸骨の owner 別同時数上限増 (陣営召喚上限内)。' },
        ],
        typeAffinity: ['Summoner'],
    },
    {
        id: 'set.dragon-scale',
        name: '竜鱗',
        slots: ['weaponMain', 'offHand', 'head', 'chest', 'hands', 'boots', 'belt'],
        conceptSkillId: 'set-skill.set.dragon-scale.dragonization',
        conceptSkillKind: 'active',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「竜化」獲得。一定時間ブレス範囲攻撃 + 被ダメ軽減。',
            },
            { piecesRequired: 4, description: '竜化の持続延長 + 物理ダメージ %。' },
            { piecesRequired: 6, description: 'ブレス範囲拡大 + 被ダメ軽減率増。' },
            { piecesRequired: 7, description: '竜化中の常時威圧オーラ。' },
        ],
        typeAffinity: ['Tank', 'MeleeDPS'],
    },
    {
        id: 'set.rune-empower',
        name: '魔力付与',
        slots: ['weaponMain', 'head', 'amulet', 'ring1', 'cloak'],
        conceptSkillId: 'set-skill.set.rune-empower.rune-enchant',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「ルーン付与」獲得。周囲味方の通常攻撃に自分の最大属性をエンチャント。',
            },
            { piecesRequired: 3, description: 'エンチャント威力増 + 支援系ステータス %。' },
            { piecesRequired: 5, description: '効果範囲拡大、全味方へ付与。' },
        ],
        typeAffinity: ['Support'],
    },
    {
        id: 'set.magus-union',
        name: '法騎連理',
        slots: ['weaponMain', 'offHand', 'head', 'chest', 'hands', 'amulet'],
        conceptSkillId: 'set-skill.set.magus-union.dual-reflection',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「連理反照」獲得。物理→魔法→物理 と交互にヒットするほど倍率上昇。',
            },
            { piecesRequired: 4, description: '倍率上限増 + 物理/魔法ダメージ %。' },
            { piecesRequired: 6, description: '交互ヒットの猶予時間延長。' },
        ],
        typeAffinity: ['MeleeDPS', 'MagicDPS'],
    },
    {
        id: 'set.inviolable-sanctuary',
        name: '不可侵の聖域',
        slots: ['weaponMain', 'head', 'chest', 'hands', 'boots', 'belt', 'amulet', 'cloak'],
        conceptSkillId: 'set-skill.set.inviolable-sanctuary.sanctuary-field',
        conceptSkillKind: 'active',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「聖域展開」獲得。範囲内の味方の致死を一度だけ無効化。',
            },
            { piecesRequired: 4, description: '聖域の範囲拡大 + 回復力 %。' },
            { piecesRequired: 6, description: '致死無効の再使用 CD 短縮。' },
            { piecesRequired: 8, description: '聖域内の味方を継続回復。' },
        ],
        typeAffinity: ['Healer'],
    },
];
// =============================================================================
// カテゴリ B: シナジー軸特化セット (4) — 中型・2 段階
// =============================================================================
const CATEGORY_B = [
    {
        id: 'set.mark-accumulation',
        name: '印の蓄積',
        slots: ['weaponMain', 'head', 'chest', 'amulet'],
        conceptSkillId: 'set-skill.set.mark-accumulation.mark-acceleration',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「印加速」獲得。印付与速度上昇、5 stack で属性爆発。',
            },
            { piecesRequired: 4, description: '属性爆発の威力増 + 属性ダメージ %。' },
        ],
    },
    {
        id: 'set.plague-spread',
        name: '疫病の伝播',
        slots: ['chest', 'hands', 'cloak', 'ring1'],
        conceptSkillId: 'set-skill.set.plague-spread.contagion',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「伝染」獲得。デバフ付き敵の隣接敵へデバフが伝播。',
            },
            { piecesRequired: 4, description: '伝播範囲拡大 + 状態異常関連ステータス %。' },
        ],
    },
    {
        id: 'set.fortress-architect',
        name: '要塞建築',
        slots: ['head', 'chest', 'belt', 'offHand'],
        conceptSkillId: 'set-skill.set.fortress-architect.expansion',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「増設」獲得。設置物の同時設置数 +1、持続延長 (template 別 owner 上限のみ増加、陣営設置物上限 24 は不変)。',
            },
            { piecesRequired: 4, description: '設置物の効果量増 + 設置物関連ステータス %。' },
        ],
    },
    {
        id: 'set.killing-chain',
        name: '殺戮の連鎖',
        slots: ['weaponMain', 'head', 'chest', 'hands'],
        conceptSkillId: 'set-skill.set.killing-chain.chain-sustain',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「連鎖維持」獲得。kill_streak が被弾で切れにくく、streak ボーナス倍率増。',
            },
            { piecesRequired: 4, description: 'kill_streak 上限増 + 攻撃力 %。' },
        ],
    },
];
// =============================================================================
// カテゴリ C: 属性特化セット (3) — 中小型・2-3 段階
// =============================================================================
const CATEGORY_C = [
    {
        id: 'set.conflagration',
        name: '業火',
        slots: ['weaponMain', 'chest', 'amulet'],
        conceptSkillId: 'set-skill.set.conflagration.flame-spread',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「延焼」獲得。燃焼中の敵が死ぬと炎が周囲に飛び火。',
            },
            { piecesRequired: 3, description: '飛び火の威力増 + 火属性ダメージ %。' },
        ],
    },
    {
        id: 'set.absolute-zero',
        name: '絶対零度',
        slots: ['head', 'chest', 'hands', 'boots'],
        conceptSkillId: 'set-skill.set.absolute-zero.frost-zone',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「凍域」獲得。凍結付与時に周囲の敵もスロー化。',
            },
            { piecesRequired: 4, description: 'スロー効果増 + 氷属性ダメージ %。' },
        ],
    },
    {
        id: 'set.abyss',
        name: '深淵',
        slots: ['chest', 'hands', 'cloak', 'ring1'],
        conceptSkillId: 'set-skill.set.abyss.erosion',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「侵食」獲得。暗黒ダメージで敵の最大 HP を一時的に削る。',
            },
            { piecesRequired: 4, description: '最大 HP 削り量増 + 闇属性ダメージ %。' },
        ],
    },
];
// =============================================================================
// カテゴリ D: 戦術コンセプトセット (3) — 中小型・2-3 段階
// =============================================================================
const CATEGORY_D = [
    {
        id: 'set.last-stand',
        name: '背水の陣',
        slots: ['weaponMain', 'chest', 'hands'],
        conceptSkillId: 'set-skill.set.last-stand.backwater',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「背水」獲得。HP が低いほど与ダメ増 (HP 25% で最大)。',
            },
            { piecesRequired: 3, description: '与ダメ増加上限増 + 攻撃力 %。' },
        ],
    },
    {
        id: 'set.bulwark',
        name: '鉄壁',
        slots: ['weaponMain', 'head', 'chest', 'hands', 'boots', 'belt', 'offHand'],
        conceptSkillId: 'set-skill.set.bulwark.bastion',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「鉄壁」獲得。シールド総量に比例し与ダメ増、戦闘中シールド常時生成。',
            },
            { piecesRequired: 4, description: 'シールド生成量増 + 防御系ステータス %。' },
            { piecesRequired: 7, description: 'シールド→与ダメ変換率増。' },
        ],
    },
    {
        id: 'set.attrition',
        name: '不屈の長期戦',
        slots: ['head', 'chest', 'belt', 'boots'],
        conceptSkillId: 'set-skill.set.attrition.endurance',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「持久強化」獲得。戦闘経過時間に比例してステータスが段階的に上昇。',
            },
            { piecesRequired: 4, description: 'ステータス上昇幅増 + 最大 HP %。' },
        ],
    },
];
// =============================================================================
// カテゴリ E: ギミック / アクセ / Luck セット (3) — 小型・2-3 段階
// =============================================================================
const CATEGORY_E = [
    {
        id: 'set.tri-relic',
        name: '三宝具',
        slots: ['amulet', 'ring1', 'ring2'],
        conceptSkillId: 'set-skill.set.tri-relic.relic-release',
        conceptSkillKind: 'active',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「宝具解放」獲得。現在 HP 帯で攻撃 / 防御 / 支援モードに分岐。',
            },
            { piecesRequired: 3, description: '各モードの効果量増 + 全ステータス %。' },
        ],
    },
    {
        id: 'set.reactive-engine',
        name: '反応閾値機関',
        slots: ['belt', 'cloak', 'ring1', 'ring2'],
        conceptSkillId: 'set-skill.set.reactive-engine.threshold-link',
        conceptSkillKind: 'passive',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「閾値連結」獲得。1 つのリアクティブ発火が別の発火を短時間連鎖させる。',
            },
            { piecesRequired: 4, description: '連鎖回数増 + リアクティブ発火頻度 %。' },
        ],
    },
    {
        id: 'set.fortune-seeker',
        name: '幸運の探求者',
        slots: ['belt', 'cloak', 'amulet'],
        conceptSkillId: 'set-skill.set.fortune-seeker.fortune',
        conceptSkillKind: 'meta',
        bonuses: [
            {
                piecesRequired: 2,
                description: 'コンセプトスキル「幸運」獲得。ダンジョン報酬のドロップ率・品質を上昇 (sim 戦闘外のメタ効果、drop ロジック連動)。',
            },
            { piecesRequired: 3, description: 'ドロップ率・ドロップ品質補正の増加。' },
        ],
    },
];
// =============================================================================
// SET_DEFINITIONS export (catalogue 完成 = 22 セット)
// =============================================================================
export const SET_DEFINITIONS = [
    ...CATEGORY_A,
    ...CATEGORY_B,
    ...CATEGORY_C,
    ...CATEGORY_D,
    ...CATEGORY_E,
];
//# sourceMappingURL=sets.js.map